import { Component, registerComponent, Entity, DetailEvent, MultiPropertySchema, AEntity } from "aframe";
import { Object3D } from "three";
import { parseExtraAttributes } from "./collection-view";

interface DataViewComponentData
{
	element : string; // Type of element to create, if templateEntity is null. Default : <a-entity/>.
	templateEntity : Entity; // Element to clone. Default : null.
	attribute : string; //The attribute that will receive the item's value. Default : none ("").
	indexAttribute : string; //The attribute that will receive the item's index/key. Default : none ("").
	indexProperty : string; //The property that will receive the item's index/key, if indexAttribute is set. Default : none ("").
	extraAttributes : string[]; //Constant attributes to be added to items. Syntax : [ "attrName { : or = } style-formatted-properties ; " ]
	filter : string[]; //Conditions that value must meet to be included. Syntax : [ "path.to.property" or "path.to.property : regex" ]
	mapping : string[]; //Dynamic attributes to be added to items. Syntax : [ "path.to.source.property : path.to.dest.property" or "path.to.source.property : regex => path.to.dest.property" ]
};

const createViewComponentSchema = (properties : MultiPropertySchema<any>) =>
{
	const schema : MultiPropertySchema<any> = {
		element : { type : "string", default : "a-entity" },
		templateEntity : { type : "selector" },
		attribute :  { type : "string", default : "" },
		indexAttribute :  { type : "string", default : "" },
		indexProperty :  { type : "string", default : "" },
		extraAttributes : { type : "array", default : [] }, // "attrName { : or = } style-formatted-properties ; "
		filter : { type : "array", default : [] }, // "path.to.property" or "path.to.property : regex"
		mapping : { type : "array", default : [] } // "path.to.source.property : path.to.dest.property" or "path.to.source.property : regex => path.to.dest.property" 
	};
	for(let key in properties)
		schema[key] = properties[key];
	return schema;
}

interface ComponentViewComponent extends Component
{
	sourceListener : EventListener;
	data : DataViewComponentData & {
		source : Entity;
		component : string;
	}
}

interface ArrayViewComponent extends Component
{
	data : DataViewComponentData & {
		source: string[]
	};
}
interface RangeViewComponent extends Component
{
	data : DataViewComponentData & {
		from : number;
		to : number;
		step : number;
	};
}


interface ApiViewComponent extends Component
{
	data : DataViewComponentData & {
		url : string;
		method : string;
		authorization : string;
		body : string;
		contentType : string;
		root : string[];
	}
}

interface ModelViewComponent extends Component
{
	data : DataViewComponentData & {
		key : string;
		pathSeparator : string;
	};
}




type ElementFactory = (index : number|string, value : any) =>  Entity | null;


const cloneValue = (value : any) =>
{
	if(typeof value !== 'object') return value;
	if(!value) return value;
	if(Array.isArray(value))
		return value.slice(0);
	
	if(typeof value.clone === "function")
		return value.clone();

	const clone : any = new value.constructor;
	for(let key in value)
		clone[key] = value[key];
	return clone;
}


const createElementFactory = (component : Component, parentEl? : Entity) =>
{
	const data = component.data as DataViewComponentData;
	const element = data.element, templateEntity = data.templateEntity, attribute = data.attribute;
	const extras = parseExtraAttributes(data.extraAttributes);
	const filter = data.filter, mapping = parseMapping(data.mapping);
	const indexAttribute = data.indexAttribute, indexProperty = data.indexProperty;

	if(attribute)
		console.warn("attribute is obsolete : can be achieved with mapping");

	const filters : ((value : any) => any)[] = ( filter || [] ).map( (f) =>
	{
		const ix = f.indexOf(":");

		const rex = (ix >= 0) ? new RegExp(f.slice(ix+1).trim()) : null;

		const path = ((ix >= 0) ? f.slice(0, ix) : f).split('.').map(trim);
		return (value : any) =>
		{
			for(let p of path)
				value = (value && (typeof value == 'object')) ? value[p] : undefined;

			if(rex)
				return (value !== undefined) && (value !== null) && rex.test(String(value));

			return value;
		};
	});


	
	const createElement : ElementFactory = (index : number|string, value : any) =>
	{
		for(let item of filters)
		{
			const itemValue = item(value);
			if(itemValue === undefined) return null;
			if(itemValue === null) return null;
			if(itemValue === false) return null;
			if(itemValue === "") return null;
		}

		if(mapping)
		{
			//Add mapped values over extras :
			const mapped : { [key:string] : any } = cloneValue(extras);
			if(attribute)
				mapping(value, mapped[attribute] || (mapped[attribute] = {} ));
			else
				mapping(value, mapped);
			value = mapped;
		}

		const el = (templateEntity ? templateEntity.cloneNode(true) : document.createElement(element)) as Entity;
		el.classList.add( component.name + "-item" );
		if(component.id)
			el.classList.add( component.name + "-" + component.id + "-item" );

		if(attribute && !mapping)
			el.setAttribute(attribute, value);
		else if(value && (value.constructor === Object))
		{
			for(let key in value)
			{
				const kvalue = value[key];
				if( (key == "dataset") && kvalue && (typeof(kvalue) === 'object') ) //Assign a dictionary to dataset :
				{
					for(let k in kvalue)
						el.dataset[k] = kvalue[k];
					continue;
				}

				el.setAttribute(key, kvalue);
			}
		}


		
		if(indexAttribute)
			el.setAttribute(indexAttribute, indexProperty ? { [indexProperty] : index } : index );
		
		if(!mapping) //Else already baked with mapped values
			for(let key in extras)
				el.setAttribute(key, extras[key]);

		if(parentEl)
			parentEl.append(el);


		const destroyListener = (event : Event) =>
		{
			//component.el.removeEventListener("componentchanged", resetListener as EventListener);
			component.el.removeEventListener("reset-view", resetListener as EventListener);
			el.removeEventListener("destroy-view-item", destroyListener);
		};
		
		const resetListener = (event : Event) =>
		{
			if((event as CustomEvent).detail !== component) return;
			el.emit('destroy-view-item', null, false);
			el.remove();
		};
		//component.el.addEventListener("componentchanged", changeListener);
		//component.el.addEventListener("componentremoved", changeListener);
		component.el.addEventListener("reset-view", resetListener);

			

		return el;
	}

	return createElement;
}



type Mapper = (value : any, result : any) => any;

const trim = (text : string) => text.trim();
const split = (text : string) => text.length ? text.split('.').map(trim) : [];

const parseMapping = (mapping : string[]) =>
{
	if(!(mapping && mapping.length)) return null;

	const mappers : Mapper[] = [];
	for(let item of mapping)
	{
		const ix = item.indexOf(":"); // path.to.src.property [ : [regex =>] path.to.dst.property ]
		
		
		const srcPath : string[] = [], dstPath : string[] = [];
		let srcRex : RegExp | null = null;
		if(ix < 0) //Single field : copy
		{
			srcPath.push( ...split(item.trim()) );
			dstPath.push( ...srcPath );
		}
		else
		{
			srcPath.push( ...split(item.slice(0, ix).trim()) );

			const dstValue = item.slice(ix+1).trim();
			const jx = dstValue.lastIndexOf("=>");
			if(jx >= 0)
			{
				srcRex = new RegExp(dstValue.slice(0, jx).trim());
				dstPath.push( ...split(dstValue.slice(jx+2).trim()) );
			}
			else
				dstPath.push( ...split(dstValue) );
		}

		const dstKey = dstPath.pop()!;
		const mapper : Mapper = (value : any, result : any) =>
		{
			for(let p of srcPath)
			{
				if(value && (typeof(value) == 'object'))
					value = value[ p ];
				else
				{
					value = undefined;
					break;
				}
			}

			if(value === undefined) return;

			const srcMatch = srcRex ? String(value).match(srcRex) : null;

			if(srcRex && !srcMatch)
			{
				console.warn("No match for " + srcRex.source + " in " + value);
				return;
			}

			for(let p of dstPath)
			{
				if(!result || (typeof result !== 'object'))
				{
					result = undefined;
					break;
				}
				let key : number | string = p, def = {};
				if(key.endsWith('[]')) //Hint that the value should be an array
				{
					key = key.slice(0, -2).trimEnd();
					def = [];
				}
				result = (key in result) ? result[key] : (result[key] = def);
			}

			if(srcMatch)
			{
				if(srcMatch.groups) //Named capture groups : merge with existing value
				{
					const currentValue = dstKey ? ( result[ dstKey ] || ( result[ dstKey ] = {} ) ) : result;
					for(let key in srcMatch.groups)
						currentValue[key] = srcMatch.groups[key];
					return;
				}
				else //Use the matched capture groups as an array
					value = srcMatch.slice(1);
			}

			if(dstKey) //Set a specific attribute
			{
				result[ dstKey ] = value;
			}
			else if(value && (typeof value === 'object'))
			{
				for(let key in value)
					result[ key ] = value[ key ];
			}
			else
				console.warn("Can't assign", value, "to", result);


		};
		mappers.push(mapper);
	}
	return (value:  any, result : any) =>
	{
		for(let m of mappers)
			m(value, result);
	};
}



registerComponent("component-view", {
	
	schema : createViewComponentSchema( {
		source : { type : "selector" },
		component : { type : "string" }
	} ),

	init(this : ComponentViewComponent)
	{
		this.sourceListener = ((event : Event & { detail : { name : string, id : string }}) =>
		{
			if(event.target !== this.data.source) return;
			const attrName = event.detail.id ? (event.detail.name + "__" + event.detail.id) : event.detail.name;
			if(attrName != this.data.component) return;

			this.update(null);

		}  ) as EventListener;
	},

	update(this : ComponentViewComponent, oldData : any)
	{
		this.el.emit('reset-view', this, false);

		const sourceComponent = this.data.source && this.data.source.components && this.data.component && this.data.source.components[ this.data.component ];

		if( oldData && (oldData.source !== this.data.source) )
		{
			if(oldData.source)
			{
				oldData.source.removeEventListener("componentinitialized", this.sourceListener );
				oldData.source.removeEventListener("componentchanged", this.sourceListener );
				oldData.source.removeEventListener("componentremoved", this.sourceListener );
			}
			if(this.data.source)
			{
				this.data.source.addEventListener("componentinitialized", this.sourceListener );
				this.data.source.addEventListener("componentchanged", this.sourceListener );
				this.data.source.addEventListener("componentremoved", this.sourceListener );
			}
		}

		if(sourceComponent && sourceComponent.data)
		{
			const createElement = createElementFactory(this, this.el);

			for(let key in sourceComponent.data)
				createElement(key, sourceComponent.data[key]);
		}
	}

});







registerComponent("array-view", {
	
	schema : createViewComponentSchema({
		source: { type: "array", default : [] }
	}),

	update(this : ArrayViewComponent, oldData : any)
	{
		this.el.emit('reset-view', this, false);

		const source = this.data.source || [], factory = createElementFactory(this, this.el);
		for(let ix = 0; ix < source.length; ++ix)
			factory(ix, source[ix]);
	}
});




registerComponent("range-view", {
	
	schema : createViewComponentSchema({
		from: { type: "number", default : 0 },
		to : { type : "number", default : 1 },
		step : { type : "number", default : 1 }
	}),

	update(this : RangeViewComponent, oldData : any)
	{
		this.el.emit('reset-view', this, false);

		const factory = createElementFactory(this, this.el);
		const step = Math.abs(this.data.step), to = this.data.to;
		let index = 0, value = this.data.from;
		if(value < to)
		{
			for(; value <= to; value += step)
				factory(index, value);
		}
		else if(value > to)
		{
			for(; value >= to; value -= step)
				factory(index, value);
		}
	}
});








registerComponent("api-view", {

	schema : createViewComponentSchema( {
		url : { type : "string" },
		method : { type : "string", default : "GET" },
		body : { type : "string" },
		authorization : { type : "string" },
		contentType : { type : "string"},
		root : { type : "array", default : [] } //Path to the collection from the document
	} ),


	update(this : ApiViewComponent, oldData : any)
	{
		this.el.emit('reset-view', this, false);

		const url = this.data.url;
		if(!url)
			return; //empty url = empty view

		const createElement = createElementFactory(this, this.el);

		const request : RequestInit = { method : this.data.method, headers : {} };
		if( (request.method != "GET") && this.data.body )
		{
			request.body = this.data.body;
			if(this.data.contentType)
				(request.headers as any)["Content-Type"] = this.data.contentType;
		}

		if(this.data.authorization)
			(request.headers as any).Authorization = this.data.authorization;
		else
			this.el.emit("authenticate-request", request );

		fetch(url, request).then( async (response) =>
		{
			let payload : any = await response.json();

			for(let rootKey of this.data.root)
			{
				if(Array.isArray(payload))
					payload = payload[ parseInt(rootKey) ];
				else if(payload && (typeof payload === "object"))
					payload = payload[ rootKey ];
			}

			if(Array.isArray(payload))
			{
				for(let ix = 0; ix < payload.length; ++ix )
				{
					createElement(ix, payload[ix]);
				}
			}
			else if(payload && (typeof payload === "object"))
			{
				for(let key in payload)
				{
					createElement(key, payload[key]);
				}
			}
		} );
	}
	

});






const buildModelView = (parentEl : Entity, obj : Object3D & { el? : Entity, originEl? : Entity }, factory : ElementFactory, prefix : string, separator : string, currentEl : Entity ) =>
{
	let path = prefix + obj.name;

	if( (obj.el === currentEl) || (obj.originEl === currentEl) )
	{
		const el = factory(path, obj);

		if(el)
		{
			obj.originEl = currentEl; //Mark obj as "taken from another Entity"
			obj.el = el;
			parentEl.append(el);
			parentEl.object3D.remove(el.object3D);
			el.object3D = obj;
			el.emit("object3dset", { type : null, object : obj }, false );
			parentEl = el;
		}
		else if(currentEl !== parentEl)
		{
			obj.originEl = currentEl;
			obj.el = parentEl;
		}
	}
	else return;

	path += separator;
	for(let child of obj.children)
	{
		buildModelView(parentEl, child, factory, path, separator, currentEl);
	}
}


registerComponent("model-view", {

	multiple : true,

	schema : createViewComponentSchema( {
		key : { type: "string", default : "model-instance" },
		pathSeparator : { type: "string", default : "/" }
	} ),

	init(this : ModelViewComponent) {

		this.el.addEventListener("object3dset", (event) =>
		{
			if(event.detail.type === this.data.key)
			{
				this.update(null);
			}
		});
	},

	update(this : ModelViewComponent, oldData : any)
	{
		this.el.emit('reset-view', this, false);

		const root = this.data.key ? this.el.object3DMap[this.data.key] : this.el.object3D;
		if(root)
		{
			const factory = createElementFactory(this, undefined);
			for(let obj of root.children) //Don't include root in view (can't change its .el )
				buildModelView(this.el, obj, factory, "", this.data.pathSeparator, this.el)
		}

	}

});





interface ElementsViewComponent extends Component
{
	data : DataViewComponentData & {
		source : HTMLElement[];
	};
}


registerComponent("elements-view", {
	schema : createViewComponentSchema( {
		source : { type : "selectorAll" }
	}),
	update(this : ElementsViewComponent, oldData : any ) 
	{
		this.el.emit('reset-view', this, false);
		
		const factory = createElementFactory(this, this.el);
		const source = this.data.source || [];
		for(let ix = 0; ix < source.length; ++ix)
		{
			factory(ix, source[ix ]);
		}
	}
} );


interface ChildrenViewComponent extends Component
{
	childAttachedListener : EventListener;
	elementFactory? : ElementFactory;
	elements : WeakMap< Element, Entity >;
	data : DataViewComponentData & {
		source : HTMLElement;
		selector : string[];
	};
}

registerComponent("children-view", {

	schema : createViewComponentSchema( {
		source : { type : "selector" },
		selector : { type : "array", default : [] }
	}),

	init(this : ChildrenViewComponent) {

		this.elements = new WeakMap( );

		this.childAttachedListener = (async (event : Event) =>
		{
			if(event.target !== this.data.source)
				return;
			if(!this.elementFactory)
				return; //Uh?

			const valueEl = (event as DetailEvent<{ el : Entity}>).detail.el;

			if(!valueEl.hasLoaded)
				await new Promise((resolve, reject) => valueEl.addEventListener("loaded", resolve ));

			const itemEl = this.elementFactory(NaN, valueEl);
			if(!itemEl)
				return; //Filtered.
			
			if(valueEl.nextElementSibling) //Not the last : insert at correct postition
			{
				let sibling : Element, siblingEl : Entity | undefined;
				for(sibling = valueEl.nextElementSibling!; sibling; sibling = sibling.nextElementSibling!)
					if((siblingEl = this.elements.get(sibling)))
						return siblingEl.before(itemEl);
				for(sibling = valueEl.previousElementSibling!; sibling; sibling = sibling.previousElementSibling!)
					if((siblingEl = this.elements.get(sibling)))
						return siblingEl.after(itemEl);
			}

			this.el.append(itemEl);		

		}) as EventListener;
	},

	update(this : ChildrenViewComponent, oldData : DataViewComponentData & { source: HTMLElement } ) 
	{
		this.el.emit('reset-view', this, false);

		const el = this.el;

		const selector = this.data.selector || [];
		const baseFactory = createElementFactory(this, undefined);
		this.elementFactory = (index : string|number, value:any)  =>
		{
			const valueEl = value as HTMLElement;

			if(selector.length)
			{
				let skip = true;
				for(let sel of selector)
					if(valueEl.matches(sel))
					{
						skip = false;
						break;
					}
				if(skip) return null;
			}
			
			const itemEl = baseFactory(index, value);
			if(!itemEl) return itemEl;

			const disconnectedListener  = (event : DetailEvent<{ el : Entity }>) =>
			{
				const el = event.detail.el;
				if(el === valueEl) //Removed from parent <=> no longer in the view
				{
					itemEl.emit("destroy-view-item", null, false);
					itemEl.remove();
				}
			};
			el.sceneEl!.addEventListener("child-detached", disconnectedListener as EventListener);

			const destroyListener = (event : Event) =>
			{
				this.elements.delete(valueEl);
				el.sceneEl!.removeEventListener("child-detached", disconnectedListener as EventListener);
				itemEl.removeEventListener("destroy-view-item", destroyListener);
			};
			itemEl.addEventListener("destroy-view-item", destroyListener);

			this.elements.set(valueEl, itemEl);
			return itemEl;
		};
		
		

		const source = this.data.source, oldSource = oldData && oldData.source;
		if( oldSource && (oldSource !== source) )
			oldData.source.removeEventListener("child-attached", this.childAttachedListener);
			
		if(source)
		{
			for(let child = source.firstElementChild; child; child = child.nextElementSibling)
			{
				const itemEl = this.elementFactory(NaN, child);
				if(itemEl)
					el.append(itemEl);
			}
			source.addEventListener("child-attached", this.childAttachedListener);
		}		
	}

});